/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks.ts';
import { selectAuthToken, selectAuthTokenVerification } from '@/store/authToken/authToken.selector';
import { useWebSocket } from '@/hooks/useWebSocket.ts';
import { WSRoomNames } from '@/common/enum';
import { updatePouchDBItem, ScriptsLoader } from '@/utilities';

import { fetchAllGroups, fetchUserDMASettings } from 'store/accountsInfo/accountsInfo.thunk.ts';
import {
  fetchIncidentForParcelSync,
  fetchJPDAIncidentSetting,
  fetchUsersSavedIncidents,
} from 'store/incidents/incidents.thunk.ts';
import {
  fetchApplicants,
  fetchSelectedApplicantSettingsPWA,
} from 'store/applicants/applicants.thunk.ts';
import { Loading } from 'components/primitive';
import { setIsInitApiLoading, setWSConnection } from 'store/connection/connection.actions.ts';
import {
  selectConnectionHealth,
  selectIsInitApiLoading,
  selectWsConnection,
} from 'store/connection/connection.selector.ts';
import { parcelSyncIncidentIDSelector } from 'store/incidents/incidents.selector.ts';
import { fetchAllTeams } from 'store/teams/teams.thunk.ts';
import {
  accountGroupIDSelector,
  selectedGroupNameForParcels,
} from 'store/accountsInfo/accountsInfo.selector.ts';
import { fetchMapLayerID } from 'store/map/map.thunk.ts';
import { setLoadingState, setNonParcelMode } from 'store/accountsInfo/accountsInfo.actions.ts';

/**
 * Startup sequence. Each stage starts at most once per mount, and the map is not
 * shown until every stage the account needs has settled.
 *
 *   mount ──▶ [1] startup chain: settings ──▶ parcel-sync incident ──▶ saved
 *             │   incidents ──▶ palmetto groups ──▶ JPDA ──▶ applicant
 *             │   settings ──▶ applicants ──▶ map layer
 *             │
 *             └──▶ dataFetched ──┬──▶ [2] websocket connect + join rooms
 *                                └──▶ [3] teams ──▶ navigate('/map')
 *
 * `connection.isInitApiLoading` is a status output, never an input. These effects
 * write it, so listing it as a dependency made them re-trigger themselves: the
 * whole chain ran a second time the instant the first run cleared the flag, and
 * that second run raced the already-mounted map -- re-nulling the parcel-sync
 * incident and the parcel county name underneath it. Keep it out of the arrays
 * below, and keep each stage behind its own start latch.
 */
const LoadingContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const auth = useAppSelector(selectAuthTokenVerification);
  const token = useAppSelector(selectAuthToken);
  const isApiLoading = useAppSelector(selectIsInitApiLoading);
  const health = useAppSelector(selectConnectionHealth);
  const wsConnection = useAppSelector(selectWsConnection);
  const selectedIncidentForParcelSync = useAppSelector(parcelSyncIncidentIDSelector);
  const selectedGroupID = useAppSelector(accountGroupIDSelector);
  const groupNameForParcels = useAppSelector(selectedGroupNameForParcels);
  const [dataFetched, setDataFetched] = useState(false);
  const { connect, joinRoom } = useWebSocket();
  ScriptsLoader();

  // One latch per stage. Without them the stages restart whenever an unrelated
  // dependency changes mid-flight, which is what produced duplicate API chains
  // and a second WebSocket on every login.
  const startupChainStartedRef = useRef(false);
  const wsConnectStartedRef = useRef(false);
  const exitStartedRef = useRef(false);

  // Mirrored so the latched effects read the current health instead of the value
  // captured when they last ran -- `health` stays out of their dependency arrays
  // for the same reason `isApiLoading` does.
  const healthRef = useRef(health);
  healthRef.current = health;

  type CallbackFunction = () => Promise<string>;
  const fetchDataAndHandleErrors = async (callbackFn: CallbackFunction): Promise<boolean> => {
    try {
      if (healthRef.current) {
        dispatch(setIsInitApiLoading(true));
        await callbackFn();
        dispatch(setIsInitApiLoading(false));
        return true;
      } else {
        dispatch(setIsInitApiLoading(false));
        goToMap();
        return false;
      }
    } catch (error) {
      dispatch(setIsInitApiLoading(false));
      console.error('Error occurred while fetching data', error);
      // Return false so callers gate their success-only follow-up (setDataFetched/navigate)
      // behind this redirect instead of advancing past a failed load.
      exitStartedRef.current = true;
      navigate('/info', {
        state: {
          message: 'Something went wrong while loading your data. Please try again.',
          buttonText: 'RELOAD',
          action: 'reload',
        },
      });
      return false;
    }
  };

  // Single exit to the map. react-router's navigate stays callable after unmount,
  // so a late caller would otherwise be able to move the user off a screen they
  // already reached.
  const goToMap = () => {
    if (exitStartedRef.current) return;
    exitStartedRef.current = true;
    dispatch(setIsInitApiLoading(false));
    navigate('/map');
  };

  // [1] Startup chain. Runs once per mount; the map waits on `dataFetched`.
  useEffect(() => {
    if (auth === null || !token || startupChainStartedRef.current) return;
    startupChainStartedRef.current = true;
    const controller = new AbortController();
    const fetchData = async () => {
      try {
        await dispatch(fetchUserDMASettings(controller))
          .unwrap()
          .then(async () => {
            await dispatch(fetchIncidentForParcelSync(controller)).unwrap();
            await dispatch(fetchUsersSavedIncidents()).unwrap();
            await dispatch(fetchAllGroups(controller)).unwrap();
            await dispatch(fetchJPDAIncidentSetting(controller)).unwrap();
            await dispatch(fetchSelectedApplicantSettingsPWA()).unwrap();
            await dispatch(fetchApplicants()).unwrap();
            await dispatch(fetchMapLayerID());
          });

        return 'DONE';
      } catch (error) {
        console.error('Error in fetchData:', error);
        throw error;
      }
    };

    fetchDataAndHandleErrors(fetchData).then((success) => {
      if (success) setDataFetched(true);
    });

    return () => {
      // Cancel anything still in flight, so a chain the user navigated away from
      // stops writing into the store behind the screen they landed on.
      controller.abort();
      // Release the latch too. `isApiLoading` is gone from the dependency array, so
      // the effect can no longer retrigger itself; if the identity of the session
      // really does change the chain should restart rather than stay latched shut
      // against a screen that renders nothing.
      startupChainStartedRef.current = false;
      dispatch(setLoadingState(false));
    };
  }, [token, auth]);

  // [2] WebSocket. Starts once the chain has settled, and runs alongside [3]
  // rather than gating the map on a socket that may never open.
  useEffect(() => {
    if (auth === null || !token || !selectedIncidentForParcelSync || !dataFetched) return;
    if (wsConnection || wsConnectStartedRef.current) return;
    wsConnectStartedRef.current = true;

    type ConnectWsParams = {
      userId: number;
      groupId: number;
      isSuperAdmin: boolean;
      accessToken: string;
    };

    const connectWs = async ({ userId, groupId, isSuperAdmin, accessToken }: ConnectWsParams) => {
      await connect(userId, groupId, isSuperAdmin, accessToken);
    };
    connectWs({
      userId: auth.id,
      groupId: auth.pvActiveGroupID,
      isSuperAdmin: false,
      accessToken: auth.accessToken || '',
    })
      .then(() => {
        dispatch(setWSConnection(true));

        joinRoom(
          WSRoomNames.DMA_APP_IA_ROOM_NAME_BASE + selectedIncidentForParcelSync,
          WSRoomNames.DMA_APP_PARENT_ROOM_NAME_IA,
          updatePouchDBItem
        );
        joinRoom(
          WSRoomNames.DMA_APP_PA_ROOM_NAME_BASE + selectedIncidentForParcelSync,
          WSRoomNames.DMA_APP_PARENT_ROOM_NAME_PA,
          updatePouchDBItem
        );
      })
      .catch((error) => {
        // A socket failure degrades to polling-free offline behaviour; it must not
        // strand the user on the loading screen, so it is logged and not rethrown.
        wsConnectStartedRef.current = false;
        console.error('Error occurred while opening the realtime connection', error);
      });
  }, [token, auth, wsConnection, selectedIncidentForParcelSync, dataFetched]);

  // [3] Teams, then the single exit to the map. Gated on `dataFetched`, so every
  // request in [1] has already resolved by the time this branch is chosen.
  useEffect(() => {
    if (!dataFetched || exitStartedRef.current) return;

    if (!selectedIncidentForParcelSync) {
      exitStartedRef.current = true;
      dispatch(setIsInitApiLoading(false));
      navigate('/info', {
        state: {
          message: 'Please set a data gathering incident in the main Damage Assessment program.',
          buttonText: 'RELOAD',
          action: 'reload',
        },
      });
      return;
    }

    if (groupNameForParcels) {
      dispatch(setNonParcelMode(groupNameForParcels));
    }

    // No active group means fetchAllTeams has no filter to run against and would
    // reject. Go straight to the map, which reports the missing group itself,
    // instead of stalling on the loading screen.
    if (!selectedGroupID) {
      goToMap();
      return;
    }

    const fetchAllTeamsAndGroup = async () => {
      // Awaited: the map reads teams on mount, so navigating while this is still
      // in flight is what left the team picker empty on a cold start.
      await dispatch(fetchAllTeams()).unwrap();
      return 'DONE';
    };

    fetchDataAndHandleErrors(fetchAllTeamsAndGroup).then((success) => {
      if (success) goToMap();
    });
  }, [selectedIncidentForParcelSync, selectedGroupID, dataFetched]);

  if (isApiLoading) {
    return <Loading message={'App is loading, please wait...'} />;
  } else {
    return null;
  }
};

export default LoadingContainer;
